import React, { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import BedbankPackageTilePricing from './BedbankPackageTilePricing'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BedbankCancellationPolicy from './BedbankCancellationPolicy'
import BedbankOfferInclusions from './BedbankOfferInclusions'
import WalledContent from 'components/Common/WalledContent'
import { useBedbankSelectDatesWithFlights } from 'hooks/OfferPage/useBedbankSelectDates'
import AccommodationPackageTile from '../Accommodation/AccommodationPackageTile'
import AccommodationPackageTileContentArea from '../Accommodation/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from '../Accommodation/AccommodationPackageTileDetails'
import BedbankPromotionDetails from './BedbankPromotionDetails'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { countOccupantsForFlights, generateRoomOccupants, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { addToCheckoutCart, initialiseCheckout, initBedbankSession } from 'actions/CheckoutActions'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { FlightViewTypes } from 'constants/flight'
import ModalContext from 'contexts/ModalContext'
import { generateBedbankAccommodationCheckoutItems } from 'lib/checkout/accommodation/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { dateDifference } from 'lib/datetime/dateUtils'
import { createBedbankHotelItemPayload } from 'tripPlanner/utils/itemConversions/cart/bedbankHotel'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import * as Analytics from 'analytics/analytics'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { useCreateTripItemBatch } from 'tripPlanner/hooks/api/tripItem'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { addToUserCart } from 'actions/AuthActions'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import Group from 'components/utils/Group'
import BedbankChangePriceModal from './Modals/BedbankChangePriceModal'
import BedbankSoldOutModal from './Modals/BedbankSoldOutModal'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'

const defaultRooms = [generateRoomOccupants()]

interface Props {
  packageView:App.BedbankOfferPackageView;
  rate: App.BedbankRate;
  checkInDate?: string;
  checkOutDate?: string;
  offer: App.BedbankOffer;
  rooms?: Array<App.Occupants>;
  optionNumber: number;
  flightOrigin?: App.Airport;
  isInitialPackageOption?: boolean;

  subscriber?: App.OrderSubscriber;
  isSpoofed?: boolean;
  isLoggedIn?: boolean;
  originAirport?: App.Airport;
  flights?: boolean;
  tripPlannerTripId?: string;

  /**
   * Unique ID for the list of rates this rate was shown under
   * Required for analytics
   */
  rateListId: string;
  /**
   * Position this room was in the list of rooms on the offer page
   * Required for analytics
   */
  roomPosition: number;
  /**
   * Unique ID for the list of rooms on the offer page
   * Required for analytics
   */
  roomListId: string;
  source?: string;
  showOptionLabel?: boolean;
}

function BedbankRoomOptionRate(props: Props) {
  const {
    offer,
    rooms = defaultRooms,
    checkInDate,
    checkOutDate,
    flightOrigin,
    roomListId,
    roomPosition,
    isInitialPackageOption,
    rateListId,
    optionNumber,
    packageView,
    rate,
    subscriber,
    isLoggedIn,
    isSpoofed,
    originAirport,
    tripPlannerTripId,
    source,
    showOptionLabel = true,
  } = props

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)

  const isBedbankFlightBookingEnabled = useAppSelector(selectIsBedbankFlightBookingEnabled)
  const occupancyCount = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const flightsEnabled = !!offer.flights.flightsEnabled && isBedbankFlightBookingEnabled && occupancyCount < 10

  const { bundleRates, promotions } = packageView
  const bundleRate = bundleRates.find((bundleRate) => bundleRate.id === rate.id) ?? rate

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < minDay
  }, [checkInDate])

  const showModal = useContext(ModalContext)

  const openDateSelector = useBedbankSelectDatesWithFlights(packageView.package.roomId)
  const onBook = useCallback(async(
    rate: App.BedbankRate,
    ratePosition: number,
    withFlights?: boolean,
    keepBrowsing = false,
  ) => {
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      trackEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }

      showModal(<ProcessingModal isOpen message="Please wait..." />, true)

      const pkg = packageView.package

      if (currentTrip) {
        const payload = createBedbankHotelItemPayload({
          offer,
          pkg,
          rate,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          currentRegionCode,
          currencyCode: currency,
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }

      try {
        // Initiate a session
        const session = await initBedbankSession(dispatch, {
          propertyId: offer.id,
          roomTypeId: rate.roomId,
          roomRateId: rate.id,
          rateGroupId: rate.groupId,
          bedGroupId: rate.bedGroups[0].id,
          isFlightBundle: rate.isFlightBundle,
          csBooking: !!isSpoofed,
          region: currentRegionCode,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          price: rate.totals.inclusive,
          propertyFees: rate.totals.propertyFees,
          memberInclusive: rate.totals.memberInclusive,
          subscriber,
          source,
        })

        if (!('sessionId' in session)) {
          showModal(<BedbankChangePriceModal
            rate={rate}
            newPrice={session.price}
            newPropertyFees={session.propertyFees}
            onAcceptPrice={(newRate) => onBook(newRate, ratePosition, withFlights, keepBrowsing)}
          />, true)
        }

        if ('sessionId' in session) {
          const accommodationItems = generateBedbankAccommodationCheckoutItems(
            offer,
            pkg,
            rate,
            {
              checkIn: checkInDate,
              checkOut: checkOutDate,
              duration: dateDifference(moment(checkOutDate).toDate(), moment(checkInDate).toDate()).days,
            },
            rooms,
            session.sessionId,
          )
          const flightItems: Array<App.Checkout.FlightItem> = []
          if (withFlights && originAirport) {
            const flightOccupants = countOccupantsForFlights(rooms)

            flightItems.push(generateFlightCheckoutItem({
              originAirportCode: originAirport?.code,
              destinationAirportCode: offer.flights.airportCode!,
              fareType: 'return',
              viewType: FlightViewTypes.TWO_ONE_WAYS,
              occupants: flightOccupants,
              passengers: generatePassengersFromOccupants(flightOccupants),
              quotedFare: (flightPrice ?? 0) / rooms.length,
              bundledItemIds: accommodationItems.map(item => item.itemId),
            }))
          }
          dispatch(addToCheckoutCart([...accommodationItems, ...flightItems]))
          fireEcommerceGTMEvent(addToCartGTM(offer, currency, session?.price, sumUpOccupancies(rooms), 'hotel'))
          Analytics.trackEvent(addToCart({
            ratePosition,
            rateListId,
            roomPosition,
            roomListId,
            rate,
            isFlightBundle: !!withFlights,
          }))

          if (keepBrowsing) {
            showSnackbar('Your item has been added to the cart.', 'success')
            return
          }

          dispatch(navigateToCheckout())
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        showModal(<BedbankSoldOutModal />, true)
      }
    }
  }, [checkInDate, checkOutDate, createTripItems, currency, currentRegionCode, currentTrip, dispatch, flightPrice, isLoggedIn, isSpoofed, offer, openDateSelector, originAirport, packageView.package, rateListId, roomListId, roomPosition, rooms, showModal, source, subscriber, trackEvent])

  const onAddToUserCart = useCallback(async(
    rate: App.BedbankRate,
    ratePosition: number,
    withFlights?: boolean,
  ) => {
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      trackEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }

      const pkg = packageView.package

      if (currentTrip) {
        const payload = createBedbankHotelItemPayload({
          offer,
          pkg,
          rate,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          currentRegionCode,
          currencyCode: currency,
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }

      const accommodationItems = generateBedbankAccommodationCheckoutItems(
        offer,
        pkg,
        rate,
        {
          checkIn: checkInDate,
          checkOut: checkOutDate,
          duration: dateDifference(moment(checkOutDate).toDate(), moment(checkInDate).toDate()).days,
        },
        rooms,
      )
      const flightItems: Array<App.Checkout.FlightItem> = []
      if (withFlights && originAirport) {
        const flightOccupants = countOccupantsForFlights(rooms)

        flightItems.push(generateFlightCheckoutItem({
          originAirportCode: originAirport?.code,
          destinationAirportCode: offer.flights.airportCode!,
          fareType: 'return',
          viewType: FlightViewTypes.TWO_ONE_WAYS,
          occupants: flightOccupants,
          passengers: generatePassengersFromOccupants(flightOccupants),
          quotedFare: (flightPrice ?? 0) / rooms.length,
          bundledItemIds: accommodationItems.map(item => item.itemId),
        }))
      }
      fireEcommerceGTMEvent(addToCartGTM(offer, currency, rate.value, sumUpOccupancies(rooms), 'hotel'))
      const checkoutItems = [...accommodationItems, ...flightItems]
      if (flightItems.length) {
        showModal(<ProcessingModal isOpen message="Please wait..." />, true)

        // Initiate a session
        const session = await initBedbankSession(dispatch, {
          propertyId: offer.id,
          roomTypeId: rate.roomId,
          roomRateId: rate.id,
          rateGroupId: rate.groupId,
          isFlightBundle: rate.isFlightBundle,
          csBooking: !!isSpoofed,
          region: currentRegionCode,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          price: rate.totals.inclusive,
          propertyFees: rate.totals.propertyFees,
          memberInclusive: rate.totals.memberInclusive,
          subscriber,
          source,
        })

        if (!('sessionId' in session)) {
          showModal<App.BedbankRate>(<BedbankChangePriceModal
            rate={rate}
            newPrice={session.price}
            newPropertyFees={session.propertyFees}
            onAcceptPrice={(newRate) => onAddToUserCart(newRate, ratePosition, withFlights)}
          />, true)
        }

        if ('sessionId' in session) {
          // Pick regular flow and final decision to pay or only add to cart to be made at end of flight selection
          dispatch(initialiseCheckout([...accommodationItems.map(item => ({ ...item, sessionId: session.sessionId })), ...flightItems], { mode: 'vertical-select' }))
          dispatch(navigateToCheckout({ skipSpoofedModal: true }))
        }
      } else {
        const bedbankContextData = {
          ratePosition,
          rateListId,
          roomPosition,
          roomListId,
          rate,
          isFlightBundle: !!withFlights,
        }
        Analytics.trackEvent(addToCartMultiItem(checkoutItems, { isMultiCart: true, bedbankContextData }))
        dispatch(addToUserCart(checkoutItems))
      }
    }
  }, [isSpoofed, source, subscriber, checkInDate, checkOutDate, createTripItems, currency, currentRegionCode, currentTrip, dispatch, flightPrice, isLoggedIn, offer, openDateSelector, originAirport, packageView.package, rateListId, roomListId, roomPosition, rooms, showModal, trackEvent])

  const promotion = promotions[rate.id]

  return <AccommodationPackageTile>
    <AccommodationPackageTileContentArea>
      <AccommodationPackageTileDetails
        packageNumber={showOptionLabel ? optionNumber : undefined}
        promotion={promotion}
        tagLineLabel={<>
          {rate.corporate && <ProductTypeLabel productType="corporate" />}
          {promotion && <ProductTypeLabel productType="limited_time_special" />}
        </>}
      >
        {promotion && <BedbankPromotionDetails
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          offer={offer}
          promotion={promotion}
          rate={rate}
          showNonCancellation={!isInitialPackageOption}
        />}
        <Group direction="vertical" gap={8} noDisplayWhenEmpty>
          {!promotion && <BedbankCancellationPolicy
            rate={rate}
            offer={offer}
            showNonCancellation
          />}
        </Group>
      </AccommodationPackageTileDetails>
      <div>
        {promotion && <BedbankOfferInclusions
          content={promotion.rateInclusionsLong}
          inclusions={promotion.inclusions}
          maxHeight={180}
        />}
        {!promotion && <VerticalSpacer gap={4}>
          {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
        </VerticalSpacer>}
      </div>
    </AccommodationPackageTileContentArea>
    <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
      <BedbankPackageTilePricing
        promotions={promotion}
        rate={rate}
        bundleRate={bundleRate}
        rooms={rooms}
        offer={offer}
        checkIn={checkInDate}
        checkOut={checkOutDate}
        flightPrice={flightPrice}
        flightsEnabled={flightsEnabled}
        departureTooEarly={departureTooEarly}
        flightOrigin={flightOrigin}
        ratePosition={optionNumber}
        rateListId={rateListId}
        roomPosition={roomPosition}
        roomListId={roomListId}
        onBookNow={onBook}
        onAddToUserCartNow={onAddToUserCart}
      />
    </WalledContent>
  </AccommodationPackageTile>
}

type MappedProps = Pick<Props,
'checkInDate' |
'checkOutDate' |
'rooms' |
'flightOrigin' |
'subscriber' |
'isLoggedIn' |
'isSpoofed' |
'originAirport' |
'flights' |
'tripPlannerTripId'
>

export default withOfferPageAndReduxState((offerState, appState): MappedProps => ({
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
  rooms: offerState.rooms,
  flightOrigin: offerState.airport,
  subscriber: getEnquirySubscriber(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isSpoofed: appState.auth.account.isSpoofed,
  originAirport: offerState.airport,
  tripPlannerTripId: appState.tripPlanner.currentTripId,
}), BedbankRoomOptionRate)
